import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldCheck, ShieldAlert, CheckCircle2, XCircle, AlertTriangle,
  Lock, Cookie, Fingerprint, Radar, RefreshCw, KeyRound, Bell, Save,
} from "lucide-react";

/** Interactive Security Posture panel — reads + edits the DB-backed
 * `security_settings` singleton via `/api/admin/security-posture`.
 *
 * Every toggle here applies live (no restart) — headers, rate limits,
 * Staff 2FA, JWT hardening.
 */

const StatusPill = ({ ok, label }) => (
  <span
    className={
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest " +
      (ok
        ? "bg-status-delivered/10 text-status-delivered"
        : "bg-status-cancelled/10 text-status-cancelled")
    }
  >
    {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
    {label || (ok ? "On" : "Off")}
  </span>
);

const SeverityPill = ({ level }) => {
  const map = {
    high: "bg-status-cancelled/10 text-status-cancelled",
    medium: "bg-status-pending/10 text-status-pending",
    low: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest " +
        (map[level] || map.low)
      }
    >
      <AlertTriangle size={10} />
      {level}
    </span>
  );
};

const ControlRow = ({ icon: Icon, title, subtitle, children }) => (
  <div className="flex items-start gap-3 rounded-md border border-border/60 bg-background px-3 py-2.5">
    <Icon size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold leading-tight">{title}</div>
      {subtitle && (
        <div className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</div>
      )}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default function SecurityPostureCard() {
  const qc = useQueryClient();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["security-posture"],
    queryFn: () => api.get("/admin/security-posture").then((r) => r.data),
    staleTime: 15_000,
  });

  const [draft, setDraft] = useState({});
  useEffect(() => {
    if (data?.settings) setDraft(data.settings);
  }, [data?.settings]);

  const save = useMutation({
    mutationFn: (patch) => api.patch("/admin/security-posture", patch).then((r) => r.data),
    onSuccess: (out) => {
      toast.success("Security settings saved — applied live");
      qc.invalidateQueries({ queryKey: ["security-posture"] });
      if (out?.settings) setDraft(out.settings);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save"),
  });

  if (isLoading || !data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">Loading security posture…</CardContent>
      </Card>
    );
  }

  const c = data.controls || {};
  const s = draft || {};
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const dirty = data.settings && Object.keys(data.settings).some((k) => draft[k] !== data.settings[k]);

  const twofaOk = !!s.staff_2fa_enabled;
  const rateLoginOk = !!s.rate_limit_login_enabled;
  const headersOk =
    !!s.header_xfo_enabled && !!s.header_xcto_enabled && !!s.header_referrer_policy_enabled;
  const jwtHardenOk = !!s.jwt_iss && !!s.jwt_aud;

  const openIssues = (twofaOk ? 0 : 1) + (rateLoginOk ? 0 : 1) + (headersOk ? 0 : 1) + (jwtHardenOk ? 0 : 1);
  const total = 4;
  const scorePct = Math.round(((total - openIssues) / total) * 100);
  const deferred = data.deferred || [];

  const saveNow = () => save.mutate(draft);
  const reset = () => data.settings && setDraft(data.settings);

  return (
    <div className="space-y-4" data-testid="security-posture-card">
      {/* Sticky action bar */}
      {dirty && (
        <div className="sticky top-16 z-20 flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2 shadow-sm">
          <div className="text-xs text-primary">
            <AlertTriangle size={12} className="mr-1 inline" /> You have unsaved changes.
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={reset} data-testid="btn-reset-posture">Reset</Button>
            <Button size="sm" onClick={saveNow} disabled={save.isPending} data-testid="btn-save-posture">
              <Save size={13} className="mr-1" /> {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      )}

      {/* Header summary */}
      <Card>
        <CardContent className="flex items-center gap-4 p-6">
          <div
            className={
              "flex h-14 w-14 items-center justify-center rounded-full " +
              (openIssues === 0
                ? "bg-status-delivered/10 text-status-delivered"
                : openIssues >= 2
                  ? "bg-status-cancelled/10 text-status-cancelled"
                  : "bg-status-pending/10 text-status-pending")
            }
          >
            {openIssues === 0 ? <ShieldCheck size={28} /> : <ShieldAlert size={28} />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 font-display text-lg font-black">
              Security Posture
              <span className="rounded-full border border-border/60 bg-background px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {scorePct}% hardened
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {openIssues === 0
                ? "All monitored controls are enabled."
                : `${openIssues} of ${total} core controls still need to be enabled below.`}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
              Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} data-testid="btn-refresh-posture">
            <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
            <span className="ml-1">Refresh</span>
          </Button>
        </CardContent>
      </Card>

      {/* Staff 2FA */}
      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Authentication
          </div>
          <ControlRow
            icon={Fingerprint}
            title="Staff 2FA (SMS OTP)"
            subtitle="Require a 4-digit MSG91 OTP on every admin / dispatcher / accounts / sales / operator sign-in."
          >
            <Switch
              checked={!!s.staff_2fa_enabled}
              onCheckedChange={(v) => set("staff_2fa_enabled", v)}
              data-testid="tgl-2fa"
            />
          </ControlRow>

          <ControlRow
            icon={Cookie}
            title="Auth cookies (read-only)"
            subtitle={
              `HttpOnly · Secure=${c.auth_cookies?.secure ? "yes" : "no"} · SameSite=${c.auth_cookies?.samesite || "?"} · ` +
              `access ${c.auth_cookies?.access_expire_min}m · refresh ${c.auth_cookies?.refresh_expire_days}d`
            }
          >
            <StatusPill ok={!!c.auth_cookies?.secure} label="Hardened" />
          </ControlRow>

          <ControlRow
            icon={Lock}
            title="Password hashing (read-only)"
            subtitle={`${c.password_hashing?.algorithm} · ${c.password_hashing?.rounds}`}
          >
            <StatusPill ok label="bcrypt" />
          </ControlRow>
        </CardContent>
      </Card>

      {/* Rate limits */}
      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Rate limits
          </div>
          <ControlRow
            icon={Radar}
            title="/auth/login per-IP throttle"
            subtitle="Blocks credential stuffing & brute-force. Requests over the limit return 429 for 60 seconds."
          >
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min="1"
                max="1000"
                className="w-20 text-right"
                value={s.rate_limit_login_per_min ?? 20}
                onChange={(e) => set("rate_limit_login_per_min", Number(e.target.value))}
                data-testid="in-rl-login-limit"
              />
              <span className="text-xs text-muted-foreground">/min</span>
              <Switch
                checked={!!s.rate_limit_login_enabled}
                onCheckedChange={(v) => set("rate_limit_login_enabled", v)}
                data-testid="tgl-rl-login"
              />
            </div>
          </ControlRow>

          <ControlRow
            icon={Radar}
            title="/auth/2fa/verify per-IP throttle"
            subtitle="Complements the per-OTP 5-attempt cap by adding IP-level rate limiting."
          >
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min="1"
                max="1000"
                className="w-20 text-right"
                value={s.rate_limit_2fa_per_min ?? 30}
                onChange={(e) => set("rate_limit_2fa_per_min", Number(e.target.value))}
                data-testid="in-rl-2fa-limit"
              />
              <span className="text-xs text-muted-foreground">/min</span>
              <Switch
                checked={!!s.rate_limit_2fa_enabled}
                onCheckedChange={(v) => set("rate_limit_2fa_enabled", v)}
                data-testid="tgl-rl-2fa"
              />
            </div>
          </ControlRow>
        </CardContent>
      </Card>

      {/* Response headers */}
      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Security response headers
          </div>

          <ControlRow
            icon={ShieldCheck}
            title="X-Frame-Options (clickjacking defence)"
            subtitle="Prevents the site from being iframed. DENY blocks all framing; SAMEORIGIN allows same-domain frames."
          >
            <div className="flex items-center gap-2">
              <select
                className="rounded-md border border-border/60 bg-background px-2 py-1 text-xs"
                value={s.header_xfo_value || "DENY"}
                onChange={(e) => set("header_xfo_value", e.target.value)}
                data-testid="sel-xfo-value"
              >
                <option value="DENY">DENY</option>
                <option value="SAMEORIGIN">SAMEORIGIN</option>
              </select>
              <Switch
                checked={!!s.header_xfo_enabled}
                onCheckedChange={(v) => set("header_xfo_enabled", v)}
                data-testid="tgl-xfo"
              />
            </div>
          </ControlRow>

          <ControlRow
            icon={ShieldCheck}
            title="X-Content-Type-Options: nosniff"
            subtitle="Blocks MIME-sniffing based content-type confusion attacks."
          >
            <Switch
              checked={!!s.header_xcto_enabled}
              onCheckedChange={(v) => set("header_xcto_enabled", v)}
              data-testid="tgl-xcto"
            />
          </ControlRow>

          <ControlRow
            icon={ShieldCheck}
            title="Referrer-Policy"
            subtitle="Controls how much URL info leaves the browser on outbound clicks."
          >
            <div className="flex items-center gap-2">
              <select
                className="rounded-md border border-border/60 bg-background px-2 py-1 text-xs"
                value={s.header_referrer_policy_value || "strict-origin-when-cross-origin"}
                onChange={(e) => set("header_referrer_policy_value", e.target.value)}
                data-testid="sel-referrer-value"
              >
                <option value="no-referrer">no-referrer</option>
                <option value="same-origin">same-origin</option>
                <option value="strict-origin-when-cross-origin">strict-origin-when-cross-origin</option>
                <option value="origin">origin</option>
              </select>
              <Switch
                checked={!!s.header_referrer_policy_enabled}
                onCheckedChange={(v) => set("header_referrer_policy_enabled", v)}
                data-testid="tgl-referrer"
              />
            </div>
          </ControlRow>

          <ControlRow
            icon={ShieldCheck}
            title="Strict-Transport-Security (HSTS)"
            subtitle={`Forces HTTPS for max-age=${s.header_hsts_max_age || 31536000}s. Enable ONLY when your production domain is confirmed on HTTPS end-to-end.`}
          >
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="60"
                className="w-24 text-right"
                value={s.header_hsts_max_age ?? 31536000}
                onChange={(e) => set("header_hsts_max_age", Number(e.target.value))}
                data-testid="in-hsts-max-age"
              />
              <Switch
                checked={!!s.header_hsts_enabled}
                onCheckedChange={(v) => set("header_hsts_enabled", v)}
                data-testid="tgl-hsts"
              />
            </div>
          </ControlRow>

          <div className="rounded-md border border-border/60 bg-background p-3">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Content-Security-Policy (CSP)</div>
                <div className="text-[11px] text-muted-foreground">
                  Advanced. Turning this on can break third-party scripts (GTM, Razorpay etc). Test before saving to production.
                </div>
              </div>
              <Switch
                checked={!!s.header_csp_enabled}
                onCheckedChange={(v) => set("header_csp_enabled", v)}
                data-testid="tgl-csp"
              />
            </div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">CSP directive value</Label>
            <Textarea
              className="mt-1 font-mono text-[11px]"
              rows={4}
              value={s.header_csp_value || ""}
              onChange={(e) => set("header_csp_value", e.target.value)}
              disabled={!s.header_csp_enabled}
              data-testid="in-csp-value"
            />
          </div>
        </CardContent>
      </Card>

      {/* JWT hardening */}
      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            JWT hardening
          </div>

          <ControlRow
            icon={KeyRound}
            title="Issuer (iss) claim"
            subtitle="Embed a stable issuer identifier in every access + refresh token."
          >
            <Input
              className="w-56 font-mono text-xs"
              placeholder="e.g. fuelexpress.in"
              value={s.jwt_iss || ""}
              onChange={(e) => set("jwt_iss", e.target.value)}
              data-testid="in-jwt-iss"
            />
          </ControlRow>

          <ControlRow
            icon={KeyRound}
            title="Audience (aud) claim"
            subtitle="Embed an audience identifier. When set, tokens are validated against it on every decode."
          >
            <Input
              className="w-56 font-mono text-xs"
              placeholder="e.g. fuelexpress-web"
              value={s.jwt_aud || ""}
              onChange={(e) => set("jwt_aud", e.target.value)}
              data-testid="in-jwt-aud"
            />
          </ControlRow>

          <ControlRow
            icon={KeyRound}
            title="Refresh-token rotation"
            subtitle="One-shot refresh tokens. Each refresh revokes the old jti — replay attempts are rejected."
          >
            <Switch
              checked={!!s.jwt_refresh_rotation_enabled}
              onCheckedChange={(v) => set("jwt_refresh_rotation_enabled", v)}
              data-testid="tgl-jwt-rotation"
            />
          </ControlRow>
        </CardContent>
      </Card>

      {/* Read-only diagnostics */}
      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Diagnostics
          </div>
          <ControlRow
            icon={Bell}
            title="Web Push (VAPID)"
            subtitle={
              `${c.vapid_push?.public_key_configured ? "public ✓" : "public ✗"} · ` +
              `${c.vapid_push?.private_key_configured ? "private ✓" : "private ✗"} · ` +
              `${c.vapid_push?.active_subscriptions ?? 0} active subs`
            }
          >
            <StatusPill
              ok={!!c.vapid_push?.public_key_configured && !!c.vapid_push?.private_key_configured}
              label={c.vapid_push?.private_key_configured ? "Ready" : "Off"}
            />
          </ControlRow>

          <ControlRow
            icon={ShieldCheck}
            title="CMS public endpoint hardening"
            subtitle="Field allowlist active · /landing/full admin-only"
          >
            <StatusPill ok label="Locked" />
          </ControlRow>

          <ControlRow
            icon={AlertTriangle}
            title="Recent denials (24h)"
            subtitle="Audit-log entries matching 403 / 401 / denied / failed"
          >
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {c.recent_activity?.denied_or_failed_last_24h ?? 0}
            </span>
          </ControlRow>
        </CardContent>
      </Card>

      {/* Deferred backlog */}
      {deferred.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              <AlertTriangle size={12} /> Still open — turn these on above before production
            </div>
            <div className="space-y-2">
              {deferred.map((d) => (
                <div
                  key={d.id}
                  className="flex items-start gap-3 rounded-md border border-border/60 bg-background px-3 py-2"
                  data-testid={`deferred-${d.id}`}
                >
                  <SeverityPill level={d.severity} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{d.title}</div>
                    <div className="text-[11px] text-muted-foreground">{d.note}</div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">{d.id}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bottom save bar (mirror of top for long pages) */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={reset} disabled={!dirty}>Reset</Button>
        <Button onClick={saveNow} disabled={!dirty || save.isPending} data-testid="btn-save-posture-bottom">
          <Save size={14} className="mr-1" /> {save.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
